<template>
  <div class="modal" :class="[
    { 'modal-open': isOpen },
  ]">
    <div class="modal-background" @click.self="hide"></div>
    <div class="modal-overlay">
      <div class="modal-header">
        <h2 class="modal-header__title">
          No Variable Personalization
        </h2>
        <button class="modal-header__close" @click="hide">
          <i class="modal-header__close-icon">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd"
                d="M0.292961 0.292826C0.683522 -0.0976612 1.31669 -0.0976008 1.70717 0.292961L6.99899 5.58579L12.293 0.292826C12.6835 -0.0976612 13.3167 -0.0976009 13.7072 0.292961C14.0977 0.683522 14.0976 1.31669 13.707 1.70717L8.41371 6.9995L13.7071 12.2929C14.0976 12.6834 14.0976 13.3166 13.7071 13.7071C13.3166 14.0976 12.6834 14.0976 12.2929 13.7071L7 8.41421L1.70711 13.7071C1.31658 14.0976 0.683417 14.0976 0.292893 13.7071C-0.0976311 13.3166 -0.0976311 12.6834 0.292893 12.2929L5.58528 7.00051L0.292826 1.70704C-0.0976612 1.31648 -0.0976008 0.683313 0.292961 0.292826Z"
                fill="black" />
            </svg>
          </i>
        </button>
      </div>
      <div class="modal-body">
        <div class="modal-body__content">
          <p class="text-block">
            You did not add a variable text option to personalize multiple trophies. Order Multiple only works if you personalize the names using <b>the variable text tool on the left panel.</b>
          </p>
          <div class="image-block">
            <div class="image-block__wrapper">
              <slot name="vdp_guide"></slot>
            </div>
          </div>
          <div class="actions">
            <div class="actions__wrapper">
              <button
                class="actions__button button-back"
                @click="() => toStep(E_STEP_NAME.EDITOR)"
                :disabled="isDisableActions"
              >
                Back to Editor
              </button>
              <button
                class="actions__button button-final"
                @click="() => toStep(E_STEP_NAME.APPROVAL)"
                :disabled="isDisableActions"
              >
                Final Preview
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import pinia from '../../core/pinia';
import { useModalStore } from '../../stores';
import uuid4 from '../../utils/uuid4';
import { E_MODAL } from '../../types/modals.enum';
import emitter from '../../core/emitter';
import { E_MIDDLEWARE_STEPS } from '../../types/middlewares.types';
import { E_STEP_NAME } from '../../types/components.enums';


const modalStore = useModalStore(pinia);
const isOpen = ref(false);
const currentKey = ref('');
const isDisableActions = ref(false);

function register() {
  currentKey.value = uuid4();
  modalStore.register(E_MODAL.VDP_VALIDATE_MODAL, currentKey.value);
}

function hide() {
  modalStore.hide(currentKey.value);
}

function toStep(stepName: E_STEP_NAME) {
  isDisableActions.value = true;
  emitter.on(E_MIDDLEWARE_STEPS.RESULT, (value) => {
    const status = value as boolean;
    if (status) {
      isDisableActions.value = false;
      hide();
    }
    emitter.off(E_MIDDLEWARE_STEPS.RESULT);
  });
  emitter.emit(E_MIDDLEWARE_STEPS.CHANGE, stepName);
}

onMounted(() => {
  register();
  modalStore.$subscribe((_, state) => {
    const modalId = modalStore.getModalIdByKey(currentKey.value);
    if (modalId != -1) {
      isOpen.value = state.modals[modalId].isOpen;
    }
  });
});
</script>

<style scoped lang="scss">
.modal {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-width: 100vw;
  max-height: 100vh;
  z-index: 998;
  align-items: center;
  justify-content: center;
}

.modal-open {
  display: flex;
}

.modal-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.modal-overlay {
  background-color: #fff;
  padding: 20px;
  z-index: 1000;
  max-width: 500px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: auto;
  gap: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;

  &__title {
    margin: 0;
    font-family: 'Montserrat';
    font-weight: 700;
    font-style: Bold;
    font-size: 20px;
    line-height: 26px;
    letter-spacing: 0%;
    color: #64747C;
  }

  &__close {
    background: none;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px;
    cursor: pointer;
  }
}

.modal-body {
  &__content {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }
}

.image-block {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  &__wrapper {
    background-color: #D9D9D999;
    padding: 5px 13px;
  }
}

.text-block {
  font-family: 'Montserrat';
  font-weight: 500;
  font-style: Medium;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #6A747C;
}

.actions {
  &__wrapper {
    display: flex;
    justify-content: flex-end;
    padding-top: 12px;
    gap: 7px;
  }

  &__button {
    padding: 9px 12px;
    border: none;
    background-color: #64747C;
    color: #fff;
    font-family: Montserrat;
    font-weight: 700;
    font-style: Bold;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0%;
    text-align: center;
    text-transform: uppercase;
    cursor: pointer;

    &:disabled {
      background: #E6E6E6;
      color: #B2B2B2;
      cursor: default;
    }
  }
}

.button-back {
  border: 0.5px solid #6A747C;
  color: #64747C;
  background: none;
}

.button-final {
  border: none;
  color: #ffffff;
  background-color: $brand-color;
}
</style>